/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
/* eslint-disable security/detect-object-injection */
import {
    ArrayExtensions,
    IProductInventoryInformation,
    validateCatalogId,
    getFallbackImageUrl
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Button,
    getTelemetryObject,
    IComponentNodeProps,
    IModuleProps,
    INodeProps,
    ISingleSlideCarouselProps,
    ITelemetryContent,
    NodeTag,
    SingleSlideCarousel
} from '@msdyn365-commerce-modules/utilities';
import { ProductComponent } from '../../themes/dobbies/views/components/product.component';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    ProductPrice,
    ProductSearchResult,
    ProjectionDomain,
    ProductWarehouseInventoryAvailability
} from '@msdyn365-commerce/retail-proxy';
import {
    getActivePricesAsync,
    getByIdAsync,
    getDeliveryOptionsAsync,
    getEstimatedAvailabilityAsync,
    searchByCriteriaAsync,
    getVariantsByDimensionValuesAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProductSearchCriteria, SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import classnames from 'classnames';
import * as React from 'react';
import { GetPlantPotsProductCountInput } from '../../actions/get-plant-pots-product-count';
import { mapProductInventoryInformation } from '../../themes/dobbies/utilities/inventory/product-inventory-utils';
import { IDobbiesProductCollectionData } from './dobbies-product-collection.data';
import { IDobbiesProductCollectionProps, layout } from './dobbies-product-collection.props.autogenerated';
import { CartModulesStateShareInput } from '../../actions/cart-state-share';
import { reaction } from 'mobx';
import { DiscountLine } from '@msdyn365-commerce/retail-proxy';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { _getCartItemMaxQuantity } from '../dobbies-cart/components/dobbies-cart-line-items';
import { getCatalogId } from '@msdyn365-commerce/core';
import { IActivePointsData } from '../dobbies-active-image';
import { IDobbiesMiniCartSummaryProduct } from '../../themes/dobbies/views/buybox.view';

export interface IProductCollectionViewProps extends IDobbiesProductCollectionProps<{}> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
    productPrices?: ProductPrice[];
    shouldShowAddAllProducts?: boolean;
    addAllProductsOnClick?: Button;
    pdpProduct?: SimpleProduct;
    pdpProductComponent?: React.ReactNode;
    isBundlesModal?: boolean;
    state: IProductComponentState;
    notificationModalProducts?: IDobbiesMiniCartSummaryProduct[];
    data: IDobbiesProductCollectionData;
}

export interface IProductBundlesMaxAvailableQuantities {
    productId: number;
    maxQuantity: number;
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
    addToCartButton: React.ReactNode;
}
export interface IProductComponentState {
    products: ProductSearchResult[];
    /* VSI Customization - START - 13/04/21 */
    inventoryInformation: IProductInventoryInformation[];
    productCount?: number;
    allProducts?: ProductSearchResult[];
    listType?: string; // Check if listType is not plants pots, return
    isSharedStateSet?: boolean; // Check if shared state is set no need to reset it again and again
    /* VSI Customization - END */
    addedProductIdsToBundles: number[];
    currQuantities: number[][];
    pdpProduct?: SimpleProduct;
    pdpSearchResultProduct?: ProductSearchResult;
    discountLines?: (DiscountLine[] | undefined)[];
    nonClubPlusDiscount?: ProductPrice[];
    simpleProductObjs?: SimpleProduct[];
}
export interface IAddToBagActionMessageState {
    productId?: number;
    itemKey?: string;
    userMessage?: string;
    statusCssString?: string;
    messageGlyph?: string;
    isOpen?: boolean;
}

export interface IProductWithModes extends ProductSearchResult {
    isCnC: boolean;
    isHomeDelivery: boolean;
}
export interface IDobbiesAddedProductToBundle {
    productId?: number;
    quantity?: number;
    price?: number;
    maxQuantity?: number;
}
/**
 * ProductCollection component
 */
class ProductCollection extends React.PureComponent<
    IDobbiesProductCollectionProps<IDobbiesProductCollectionData>,
    IProductComponentState & IAddToBagActionMessageState
> {
    private telemetryContent: ITelemetryContent;
    private productPrices: ProductPrice[];
    constructor(
        props: IDobbiesProductCollectionProps<IDobbiesProductCollectionData>,
        state: IProductComponentState & IAddToBagActionMessageState
    ) {
        super(props);
        const {
            config: { productCollection }
        } = this.props;
        this.productPrices = [];
        this.state = {
            products: productCollection && productCollection.products,
            inventoryInformation: [],
            productCount: 0,
            listType: productCollection && productCollection.listType,
            allProducts: [],
            addedProductIdsToBundles: [],
            currQuantities: [],
            discountLines: [],
            nonClubPlusDiscount: []
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        if (this.props.config.isNotificationModal) {
            this.getActiveImageproductAction();
        }
    }
    private getActiveImageproductAction = async () => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            }
        } = this.props;
        const cookienotificationValue = cookies.get('notification_products');
        if (cookienotificationValue && Array.isArray(cookienotificationValue.value) && cookienotificationValue.value['length'] > 0) {
            const activePoints = cookienotificationValue.value;
            const itemIds: number[] = [];
            if (activePoints) {
                activePoints.map((activePointData: IActivePointsData) => itemIds.push(+activePointData.itemId!));
            }
            const searchCriteriaInput: ProductSearchCriteria = {};
            const catalogId = getCatalogId(this.props.context.actionContext.requestContext);
            validateCatalogId(catalogId);

            searchCriteriaInput.Context = {
                ChannelId: this.props.context.actionContext.requestContext.apiSettings.channelId,
                CatalogId: catalogId
            };
            searchCriteriaInput.IncludeAttributes = true;
            searchCriteriaInput.Ids = itemIds;
            if (!activePoints || !ArrayExtensions.hasElements(activePoints)) {
                throw new Error('Fail to call ActiveImage API no active points configured.');
            }
            // eslint-disable-next-line no-return-await -- Disable because we want to return the promise.
            const products = await searchByCriteriaAsync({ callerContext: this.props.context.actionContext }, searchCriteriaInput);
            this.setState({ products: products });
            // return products
        }
    };

    public componentDidMount(): void {
        const {
            config: { allowedRelationType, productCollection },
            context: { actionContext }
        } = this.props;
        if (!this.props.config.isProductBundles) {
            void this.getNonClubPlusDiscountPrices();
        }
        /* VSI Customization - START - 13/04/21 */
        // Check if listType is not plants pots, return
        if (!this.state.isSharedStateSet) {
            if (this.state.listType?.toLowerCase() === allowedRelationType?.toLowerCase() && productCollection?.products?.length > 0) {
                this._getProductAvailability()
                    .then(res => {
                        const cookies = this.props.context.actionContext.requestContext.cookies;
                        const maxQData: IProductBundlesMaxAvailableQuantities[] = [];
                        for (const sp of this.state.products) {
                            const maxQ = this.getMaxAvailableQuantityForProduct(sp.RecordId);
                            const maxQObj: IProductBundlesMaxAvailableQuantities = { productId: sp.RecordId, maxQuantity: maxQ };
                            maxQData.push(maxQObj);
                        }
                        cookies.set('product_bundles_maximum_available_quantities', maxQData);
                    })
                    .catch(error => error);
            } else {
                actionContext.update(new GetPlantPotsProductCountInput(), { potsProductCount: 0 });
                this.setState({ isSharedStateSet: true });
            }
        }
        /* VSI Customization - END */

        this.setState({ addedProductIdsToBundles: [] });
        this.updateAddedProductsList();
        reaction(
            () => this.props.data.cartSharedState.result?.isCartUpdated,
            () => {
                if (this.props.data.cartSharedState.result?.isCartUpdated) {
                    this.updateAddedProductsList();
                }
            }
        );
        if (this.props.config.isProductBundles && window && !this.props.config.isNotificationModal) {
            const splittedUrl = window.location.href.split('/');
            const prodIdStr = splittedUrl[splittedUrl.length - 1];
            const prodIdStrSplitted = prodIdStr.split('.p');
            let prodId = '';
            if (prodIdStrSplitted.length > 1) {
                prodId = prodIdStrSplitted[0];
            }
            if (prodId) {
                getByIdAsync(
                    { callerContext: this.props.context.actionContext },
                    Number(prodId),
                    this.props.context.request.apiSettings.channelId
                )
                    .then(resp => {
                        const pdpProd: SimpleProduct = Array.isArray(resp) ? resp[0] : resp;
                        this.setState({ pdpProduct: pdpProd });
                        const searchCriteriaInput: ProductSearchCriteria = {};
                        searchCriteriaInput.Context = {
                            ChannelId: this.props.context.actionContext.requestContext.apiSettings.channelId,
                            CatalogId: this.props.context.actionContext.requestContext.apiSettings.catalogId
                        };
                        const productId = pdpProd.MasterProductId ? pdpProd.MasterProductId : pdpProd.RecordId;
                        searchCriteriaInput.Ids = [productId];
                        searchCriteriaInput.IncludeAttributes = true;
                        searchByCriteriaAsync(
                            {
                                callerContext: this.props.context.actionContext,
                                queryResultSettings: {}
                            },
                            searchCriteriaInput
                        )
                            .then(searchByCriteriaResp => {
                                if (searchByCriteriaResp && searchByCriteriaResp.length > 0) {
                                    const searchByCriteriaItem = searchByCriteriaResp[0];
                                    searchByCriteriaItem.RecordId = pdpProd.RecordId;
                                    if (searchByCriteriaItem.IsMasterProduct) {
                                        getVariantsByDimensionValuesAsync(
                                            { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                                            searchByCriteriaItem.RecordId,
                                            this.props.context.request.apiSettings.channelId,
                                            []
                                        ).then(variants => {
                                            if (variants && variants.length) {
                                                const variant = variants[0];
                                                const fallbackImageUrl = getFallbackImageUrl(
                                                    variant.ItemId,
                                                    this.props.context.actionContext.requestContext.apiSettings
                                                );
                                                if (!variant.PrimaryImageUrl?.includes('http')) {
                                                    variant.PrimaryImageUrl = fallbackImageUrl;
                                                }
                                                this.setState({ pdpSearchResultProduct: variant });
                                                void this.getNonClubPlusDiscountPrices();
                                                void this.getAllSimpleProducts();
                                            }
                                        });
                                    } else {
                                        this.setState({ pdpSearchResultProduct: searchByCriteriaItem });
                                        void this.getNonClubPlusDiscountPrices();
                                        void this.getAllSimpleProducts();
                                    }
                                }
                            })
                            .catch(e => {});
                    })
                    .catch(ex => {});
            }
        }
    }
    public checkDifference = (array1: ProductSearchResult[], array2: ProductSearchResult[]) => {
        if (!array1 || !array2) {
            return true;
        }
        if (array1?.length !== array2?.length) {
            return true;
        }
        for (let i = 0; i < array1.length; i++) {
            if (array1[i].RecordId !== array2[i].RecordId) {
                return true;
            }
        }
        return false;
    };

    public componentDidUpdate(
        prevProps: Readonly<IDobbiesProductCollectionProps<IDobbiesProductCollectionData>>,
        prevState: Readonly<IProductComponentState & IAddToBagActionMessageState>,
        snapshot?: any
    ): void {
        if (this.checkDifference(prevState.products, this.state.products)) {
            if (this.state.products && this.props.config.isNotificationModal && this.props.config.isProductBundles) {
                this._getProductAvailabilityNotification()
                    .then(res => {
                        const cookies = this.props.context.actionContext.requestContext.cookies;
                        const maxQData: IProductBundlesMaxAvailableQuantities[] = [];
                        for (const sp of this.state.products) {
                            const maxQ = this.getMaxAvailableQuantityForProduct(sp.RecordId);
                            const maxQObj: IProductBundlesMaxAvailableQuantities = { productId: sp.RecordId, maxQuantity: maxQ };
                            maxQData.push(maxQObj);
                        }
                        cookies.set('product_notification_maximum_available_quantities', maxQData);
                        void this.getNonClubPlusDiscountPrices();
                        void this.getAllSimpleProducts();
                    })
                    .catch(error => error);
            }
        }
    }
    private async getProductsListWithKitProductsHandled(products: ProductSearchResult[]): Promise<ProductSearchResult[]> {
        const kitProductIds = products.map(prod => {
            if (prod.IsMasterProduct) {
                return prod.RecordId;
            }
            return undefined;
        });
        if (kitProductIds && kitProductIds.length) {
            for (const kpId of kitProductIds) {
                if (kpId) {
                    const variants = await getVariantsByDimensionValuesAsync(
                        { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                        kpId,
                        this.props.context.request.apiSettings.channelId,
                        []
                    );
                    if (variants && variants.length) {
                        products = products.filter(p => p.RecordId !== kpId);
                        products.push(variants[0]);
                    }
                }
            }
        }
        return products;
    }
    private async getAllSimpleProducts(): Promise<void> {
        const cookies = this.props.context.actionContext.requestContext.cookies;
        let products = this.props.config.isNotificationModal
            ? this.state.products
            : this.props.config.productCollection && this.props.config.productCollection.products;
        products = await this.getProductsListWithKitProductsHandled(products);

        if (!this.props.config.isNotificationModal) {
            const productCollectionProducts = products.map(pros => ({ itemId: pros.RecordId }));
            cookies.set('productBundle_products', productCollectionProducts);
        }
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        const simpleProds = await getByIdsAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext)
            },
            this.props.context.request.apiSettings.channelId,
            productRecIds,
            null,
            this.props.context.request.apiSettings.catalogId ?? 0
        );
        this.setState({ simpleProductObjs: simpleProds });
    }

    private _getNotificationModalProducts(): IDobbiesMiniCartSummaryProduct[] {
        const addedToSummaryProducts: IDobbiesMiniCartSummaryProduct[] = [];
        const cookies = this.props.context.actionContext.requestContext.cookies;
        const addedProductsToCollection =
            cookies.get(this.props.config.isNotificationModal ? 'notification_added_products' : 'added_products_bundles')?.value || [];
        if (Array.isArray(addedProductsToCollection)) {
            for (const obj of addedProductsToCollection) {
                const product = this.state.simpleProductObjs?.find(prod => prod.RecordId === obj['productId']);
                if (product) {
                    const miniCartProduct: IDobbiesMiniCartSummaryProduct = { ...product };
                    miniCartProduct.quantity = obj['quantity'];
                    addedToSummaryProducts.push(miniCartProduct);
                }
            }
        }
        return addedToSummaryProducts;
    }

    public render(): JSX.Element | null {
        const { heading, className } = this.props.config;
        const { resources } = this.props;
        const { products, inventoryInformation } = this.state;
        // Return if there are no products
        if (!products || products.length === 0 || inventoryInformation.length === 0) {
            return null;
        }
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingComponent = heading && (
            <Msdyn365.Text
                className='ms-product-collection__heading'
                tag={heading.tag || 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
            />
        );

        const productCollectionViewProps = {
            ...this.props,
            state: this.state,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id,
                telemetryContent: this.telemetryContent
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection product-bundles', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items'
            },
            isCarousel: isCarousel,
            products:
                products &&
                products.length > 0 &&
                products
                    .filter(item => {
                        // Check if product doesn't have inventory, dont dislay it
                        const itemInventory = inventoryInformation.find(
                            inventory => inventory.ProductAvailableQuantity.ProductId === item.RecordId
                        );

                        return itemInventory && itemInventory.IsProductAvailable === true;
                    })
                    .map((item: ProductSearchResult, index: number) => this._getProduct(item, index)),
            productPrices: this.productPrices,
            shouldShowAddAllProducts: this.props.config.isProductBundles,
            pdpProduct: this.props.config.isProductBundles && this.state.pdpProduct ? this.state.pdpProduct : undefined,
            pdpProductComponent:
                this.props.config.isProductBundles &&
                this.state.pdpSearchResultProduct &&
                this._getPDPProduct(this.state.pdpSearchResultProduct, this.state.pdpSearchResultProduct.RecordId),
            isBundlesModal: this.props.config.isBundlesModal,
            notificationModalProducts: this._getNotificationModalProducts()
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private updateAddedProductsList = () => {
        const cookies = this.props.context.actionContext.requestContext.cookies;
        const addedProductsToCollection =
            cookies.get(this.props.config.isNotificationModal ? 'notification_added_products' : 'added_products_bundles')?.value || [];
        const addedProductsIds: number[] = [];
        if (Array.isArray(addedProductsToCollection)) {
            for (const obj of addedProductsToCollection) {
                addedProductsIds.push(obj['productId']);
            }
            this.setState({ addedProductIdsToBundles: addedProductsIds });
        }
        if (
            this.props.config.isNotificationModal &&
            this.props.config.isProductBundles &&
            this.props.context.request.telemetryPageName !== 'DefaultPdp'
        ) {
            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: false });
        }
    };
    private getPDPProductMaxQ = () => {
        const cookies = this.props.context.actionContext.requestContext.cookies;
        return cookies.get('pdp_product_quantity_for_bundles').value;
    };
    private onBundleProductCheckBoxSelected = (productId: number) => {
        const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === productId);
        const maxPDPQ = this.getPDPProductMaxQ();
        const maxPDPQNumber = maxPDPQ ? Number(maxPDPQ) : 0;
        if (productId === this.state.pdpSearchResultProduct?.RecordId && !(cl && cl.Quantity && cl.Quantity >= maxPDPQNumber)) {
            this._updateCookiesForAddedProducts(this.state.pdpSearchResultProduct);
        } else {
            const prods = this.state.products;
            const prod = prods.find(p => p.RecordId === productId);
            if (prod) {
                void this._addItemToCart(prod, prod.RecordId.toString());
            }
        }
    };
    private _getPDPProduct = (product: ProductSearchResult, index: number): React.ReactNode => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { addToCartButtonText } = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;
        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');
        const resources = this.props.resources;
        //  9.33 SDK Update
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );
        console.log(reviewCountAriaLabelValue);
        // VSI - CUSTOMIZATION 04/02/2022 - To hide the Mode Of Delivery icons from modal for related products / delivery cart upsells
        const productWithModes: IProductWithModes = { ...product, isCnC: false, isHomeDelivery: false };
        let shouldNotShowAddToCartButton =
            this.props.config.isProductBundles && this.state.addedProductIdsToBundles.includes(product.RecordId);
        const maxAvailableQ = this.getPDPProductMaxQ(); //this.getMaxAvailableQuantityForProduct(productWithModes.RecordId);
        if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
            const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
            if (cl) {
                shouldNotShowAddToCartButton = true;
            } else {
                shouldNotShowAddToCartButton = false;
            }
        }
        if (this.props.config.isProductBundles && this.props.config.isNotificationModal) {
            const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
            if (cl) {
                shouldNotShowAddToCartButton = true;
            } else {
                shouldNotShowAddToCartButton = false;
            }
        }
        // VSI - CUSTOMIZATION - END
        return (
            <div className='pdpCurrentProd'>
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product: productWithModes }}
                    quickViewButton={quickviewSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    showDeliveryIcons={false}
                    wishlists={this.props.data.wishlists?.result}
                    isProductAddedToBundles={shouldNotShowAddToCartButton}
                    maxQuantityForBundle={maxAvailableQ ? Number(maxAvailableQ) : 0}
                    redirectToCartEnabled={this.props.config.redirectToCart}
                    isProductBundles={this.props.config.isProductBundles}
                    isBundlesModal={this.props.config.isBundlesModal}
                    onQuantityChangeListener={this.onQuantityChangeListener}
                    onBundleProductCheckBoxSelected={this.onBundleProductCheckBoxSelected}
                    isQuantityUpdated={this.props.data.cartSharedState.result?.isQuantityUpdated}
                    discountLine={this.state.discountLines && this.state.discountLines.length > 0 ? this.state.discountLines[index] : []}
                    nonClubPlusDiscount={this.state.nonClubPlusDiscount?.filter(item => item.ProductId === productWithModes.RecordId)[0]}
                    simpleProduct={this.state.simpleProductObjs?.find(sp => sp.RecordId === productWithModes.RecordId)}
                />
                {shouldNotShowAddToCartButton ? (
                    <></>
                ) : (
                    <Button
                        className='ms-wishlist-items__product-add-button'
                        title={addToCartButtonText}
                        onClick={this._getAddToCart(product, index.toString())}
                    >
                        Add
                    </Button>
                )}
            </div>
        );
    };

    private _getProduct = (product: ProductSearchResult, index: number): IProductComponentViewProps | null => {
        if (!product || !product.Name) {
            this.props.telemetry.trace('[product-collection] Product or product name is null');
            return null;
        }
        const telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        const { imageSettings, allowBackNavigation } = this.props.config;
        const { addToCartButtonText } = this.props.resources;
        const breaCrumbType = this.props.context.app.config.breadcrumbType;

        const allowBack = allowBackNavigation && (breaCrumbType === 'back' || breaCrumbType === 'categoryAndBack');

        const resources = this.props.resources;
        //  9.33 SDK Update
        const quickviewSlot =
            this.props.slots && this.props.slots.quickview && this.props.slots.quickview.length > 0 ? this.props.slots.quickview[0] : null;
        const reviewCount = 1;
        const reviewTotalRatings = Number(product.TotalRatings);
        const reviewCountAriaLabelValue =
            reviewTotalRatings > reviewCount ? resources.ratingCountAriaLabel : resources.ratingCountAriaLabelSingleUser;
        const isPriceMinMaxEnabled = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
        );

        console.log(reviewCountAriaLabelValue);
        // VSI - CUSTOMIZATION 04/02/2022 - To hide the Mode Of Delivery icons from modal for related products / delivery cart upsells
        const productWithModes: IProductWithModes = { ...product, isCnC: false, isHomeDelivery: false };
        let shouldNotShowAddToCartButton =
            this.props.config.isProductBundles && this.state.addedProductIdsToBundles.includes(product.RecordId);
        if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
            const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
            if (cl) {
                shouldNotShowAddToCartButton = true;
            } else {
                shouldNotShowAddToCartButton = false;
            }
        }
        if (this.props.config.isProductBundles && this.props.config.isNotificationModal) {
            const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
            if (cl) {
                shouldNotShowAddToCartButton = true;
            } else {
                shouldNotShowAddToCartButton = false;
            }
        }
        // VSI - CUSTOMIZATION - END
        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item',
                role: 'listitem',
                key: index
            },
            productComponent: (
                <ProductComponent
                    context={this.props.context}
                    telemetryContent={telemetryContent}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={resources.ratingAriaLabel}
                    allowBack={allowBack}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{ product: productWithModes }}
                    quickViewButton={quickviewSlot}
                    isPriceMinMaxEnabled={isPriceMinMaxEnabled?.IsEnabled}
                    showDeliveryIcons={false}
                    wishlists={this.props.data.wishlists?.result}
                    isProductAddedToBundles={shouldNotShowAddToCartButton}
                    maxQuantityForBundle={this.getMaxAvailableQuantityForProduct(productWithModes.RecordId)}
                    redirectToCartEnabled={this.props.config.redirectToCart}
                    isProductBundles={this.props.config.isProductBundles}
                    isBundlesModal={this.props.config.isBundlesModal}
                    isNotificationModal={this.props.config.isNotificationModal}
                    isQuantityUpdated={this.props.data.cartSharedState.result?.isQuantityUpdated}
                    onQuantityChangeListener={this.onQuantityChangeListener}
                    onBundleProductCheckBoxSelected={this.onBundleProductCheckBoxSelected}
                    discountLine={this.state.discountLines && this.state.discountLines.length > 0 ? this.state.discountLines[index] : []}
                    nonClubPlusDiscount={this.state.nonClubPlusDiscount?.filter(item => item.ProductId === productWithModes.RecordId)[0]}
                    simpleProduct={this.state.simpleProductObjs?.find(sp => sp.RecordId === productWithModes.RecordId)}
                />
            ),
            addToCartButton: shouldNotShowAddToCartButton ? (
                <></>
            ) : (
                <Button
                    className='ms-wishlist-items__product-add-button'
                    title={addToCartButtonText}
                    onClick={this._getAddToCart(product, index.toString())}
                >
                    Add
                </Button>
            )
        };
    };

    private onQuantityChangeListener = (productId: number, newQuantity: number): void => {
        const currQuantityData = this.state.currQuantities;
        let productFound = false;
        for (const qd of currQuantityData) {
            if (qd[0] === productId) {
                qd[1] = newQuantity;
                productFound = true;
            }
        }
        if (!productFound) {
            currQuantityData.push([productId, newQuantity]);
        }
        this.setState({ currQuantities: currQuantityData });
        if (this.props.config.isNotificationModal && this.props.config.isProductBundles) {
            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: false });
        }
    };

    private _getProductAvailabilityNotification = async (): Promise<void> => {
        const { products } = this.state;
        const {
            context: { actionContext }
        } = this.props;

        if (!products) {
            return;
        }

        const validProducts: number[] = [];

        for (const product of products) {
            if (product.RecordId) {
                validProducts.push(product.RecordId);
            }
        }

        if (validProducts.length === 0) {
            return;
        }

        const callerContext = actionContext;
        const {
            requestContext: {
                apiSettings: { channelId, catalogId }
            }
        } = actionContext;
        const searchCriteriaInput: ProductSearchCriteria = {};
        searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
        searchCriteriaInput.Ids = validProducts;
        searchCriteriaInput.IncludeAttributes = true;
        const productData = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);

        this.setState({
            allProducts: productData
        });
        const attributeName = 'fulfillmenttype';
        const attributeTextValue = 'plants';

        const plantProducts: ProductSearchResult[] = [];
        const nonPlantProducts: ProductSearchResult[] = [];

        this.state.allProducts &&
            this.state.allProducts.map(product => {
                const fulfillmentTypeAttribute =
                    product.AttributeValues &&
                    product.AttributeValues.find(attribute => {
                        return attribute?.Name?.toLowerCase() === attributeName;
                    });
                const isPlantFulfillment =
                    fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
                const isPlantCategory = isPlantFulfillment ? true : false;

                if (isPlantCategory) {
                    plantProducts.push(product);
                } else {
                    nonPlantProducts.push(product);
                }
            });

        const prodIds = this.state.allProducts && this.state.allProducts.map(prod => prod.RecordId);
        if (prodIds && prodIds.length <= 0) {
            return;
        }

        const productAvailabilitesPlants = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const deliveryOptions = await getDeliveryOptionsAsync(
            { callerContext: actionContext, queryResultSettings: {} },
            prodIds ? prodIds : []
        );
        const productsWithPlantsDelivery: IProductWithModes[] = await Promise.all(
            plantProducts.map(async (prod, idx) => {
                let isCnC = false;
                let isHomeDelivery = false;

                if (productAvailabilitesPlants && productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities) {
                    productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities?.map(
                        (item: ProductWarehouseInventoryAvailability) => {
                            if (item.InventLocationId === 'P001' || item.InventLocationId === 'S059') {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                                    isHomeDelivery = true;
                                }
                            } else {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                                    deliveryOptions[idx].DeliveryOptions?.map(option => {
                                        if (option.Code === 'ClickCol') {
                                            isCnC = true;
                                        }
                                    });
                                }
                            }
                        }
                    );
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }

                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        // Not Plants API since the warehouse storage is different
        const productAvailabilitesNonPlantsHD = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: true, FilterByChannelFulfillmentGroup: false }
        );
        const productAvailabilitesNonPlantsCNC = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const productsWithNonPlantsDelivery: IProductWithModes[] = await Promise.all(
            nonPlantProducts.map(async (prod, idx) => {
                let isCnC = false;
                let isHomeDelivery = false;

                if (productAvailabilitesNonPlantsHD && productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            isHomeDelivery = true;
                        }
                    });
                }

                if (productAvailabilitesNonPlantsCNC && productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            deliveryOptions[idx].DeliveryOptions?.map(option => {
                                if (option.Code === 'ClickCol') {
                                    isCnC = true;
                                }
                            });
                        }
                    });
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }

                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        const totalProducts = [...productsWithPlantsDelivery, ...productsWithNonPlantsDelivery];

        const allProducts: any = [];
        products.map(prod => {
            totalProducts.find(item => {
                if (item.RecordId === prod.RecordId) {
                    allProducts.push(item);
                }
            });
        });

        if (plantProducts.length > 0) {
            const inventoryInformation = mapProductInventoryInformation(
                actionContext,
                productAvailabilitesPlants?.ProductWarehouseInventoryAvailabilities ?? []
            );
            this.setState({ inventoryInformation: inventoryInformation, productCount: plantProducts.length });

            // Check if product shared state is not set, set it
            if (!this.state.isSharedStateSet) {
                this.props.context.actionContext.update(new GetPlantPotsProductCountInput(), {
                    potsProductCount: plantProducts.length
                });
                this.setState({ isSharedStateSet: true });
            }
        } else if (nonPlantProducts.length > 0) {
            const inventoryInformation = mapProductInventoryInformation(
                actionContext,
                productAvailabilitesNonPlantsHD?.ProductWarehouseInventoryAvailabilities ?? []
            );
            this.setState({
                inventoryInformation: inventoryInformation,
                productCount: nonPlantProducts.length
            });
            // Check if product shared state is not set, set it
            if (!this.state.isSharedStateSet) {
                this.props.context.actionContext.update(new GetPlantPotsProductCountInput(), {
                    potsProductCount: nonPlantProducts.length
                });
                this.setState({ isSharedStateSet: true });
            }
        }
    };

    /* VSI Customization - START - 13/04/21 */
    private _getProductAvailability = async (): Promise<void> => {
        const { products } = this.state;
        const {
            context: { actionContext }
        } = this.props;

        if (!products) {
            return;
        }

        const validProducts: number[] = [];

        for (const product of products) {
            if (product.RecordId) {
                validProducts.push(product.RecordId);
            }
        }

        if (validProducts.length === 0) {
            return;
        }

        const callerContext = actionContext;
        const {
            requestContext: {
                apiSettings: { channelId, catalogId }
            }
        } = actionContext;
        const searchCriteriaInput: ProductSearchCriteria = {};
        searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
        searchCriteriaInput.Ids = validProducts;
        searchCriteriaInput.IncludeAttributes = true;
        const productData = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);

        this.setState({
            allProducts: productData
        });
        const attributeName = 'fulfillmenttype';
        const attributeTextValue = 'plants';

        const plantProducts: ProductSearchResult[] = [];
        const nonPlantProducts: ProductSearchResult[] = [];

        this.state.allProducts &&
            this.state.allProducts.map(product => {
                const fulfillmentTypeAttribute =
                    product.AttributeValues &&
                    product.AttributeValues.find(attribute => {
                        return attribute?.Name?.toLowerCase() === attributeName;
                    });
                const isPlantFulfillment =
                    fulfillmentTypeAttribute && fulfillmentTypeAttribute.TextValue?.toLowerCase() === attributeTextValue;
                const isPlantCategory = isPlantFulfillment ? true : false;

                if (isPlantCategory) {
                    plantProducts.push(product);
                } else {
                    nonPlantProducts.push(product);
                }
            });

        const prodIds = this.state.allProducts && this.state.allProducts.map(prod => prod.RecordId);
        if (prodIds && prodIds.length <= 0) {
            return;
        }

        const productAvailabilitesPlants = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const deliveryOptions = await getDeliveryOptionsAsync(
            { callerContext: actionContext, queryResultSettings: {} },
            prodIds ? prodIds : []
        );
        const productsWithPlantsDelivery: IProductWithModes[] = await Promise.all(
            plantProducts.map(async (prod, idx) => {
                let isCnC = false;
                let isHomeDelivery = false;

                if (productAvailabilitesPlants && productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities) {
                    productAvailabilitesPlants.ProductWarehouseInventoryAvailabilities?.map(
                        (item: ProductWarehouseInventoryAvailability) => {
                            if (item.InventLocationId === 'P001' || item.InventLocationId === 'S059') {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                                    isHomeDelivery = true;
                                }
                            } else {
                                if (item.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                                    deliveryOptions[idx].DeliveryOptions?.map(option => {
                                        if (option.Code === 'ClickCol') {
                                            isCnC = true;
                                        }
                                    });
                                }
                            }
                        }
                    );
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }

                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        // Not Plants API since the warehouse storage is different
        const productAvailabilitesNonPlantsHD = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: true, FilterByChannelFulfillmentGroup: false }
        );
        const productAvailabilitesNonPlantsCNC = await getEstimatedAvailabilityAsync(
            { callerContext: actionContext },
            { ProductIds: prodIds, DefaultWarehouseOnly: false, FilterByChannelFulfillmentGroup: true }
        );

        const productsWithNonPlantsDelivery: IProductWithModes[] = await Promise.all(
            nonPlantProducts.map(async (prod, idx) => {
                let isCnC = false;
                let isHomeDelivery = false;

                if (productAvailabilitesNonPlantsHD && productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsHD.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            isHomeDelivery = true;
                        }
                    });
                }

                if (productAvailabilitesNonPlantsCNC && productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities) {
                    productAvailabilitesNonPlantsCNC.AggregatedProductInventoryAvailabilities.map(stores => {
                        if (stores.ProductId === prod.RecordId && stores.PhysicalAvailableInventoryLevelCode !== 'OOS') {
                            deliveryOptions[idx].DeliveryOptions?.map(option => {
                                if (option.Code === 'ClickCol') {
                                    isCnC = true;
                                }
                            });
                        }
                    });
                }

                // If product has vendorship === "yes" then show it as available
                const isVendorShipAttribute = prod.AttributeValues?.find(attribute => {
                    const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                    return attributeName === 'isvendershipproduct';
                });

                if (isVendorShipAttribute && isVendorShipAttribute.TextValue && isVendorShipAttribute.TextValue.toLowerCase() === 'yes') {
                    isHomeDelivery = true;
                    isCnC = false;
                }

                return { ...prod, isHomeDelivery, isCnC };
            })
        );

        const totalProducts = [...productsWithPlantsDelivery, ...productsWithNonPlantsDelivery];

        const allProducts: any = [];
        products.map(prod => {
            totalProducts.find(item => {
                if (item.RecordId === prod.RecordId) {
                    allProducts.push(item);
                }
            });
        });

        if (plantProducts.length > 0) {
            const inventoryInformation = mapProductInventoryInformation(
                actionContext,
                productAvailabilitesPlants?.ProductWarehouseInventoryAvailabilities ?? []
            );
            this.setState({ inventoryInformation: inventoryInformation, productCount: plantProducts.length });

            // Check if product shared state is not set, set it
            if (!this.state.isSharedStateSet) {
                this.props.context.actionContext.update(new GetPlantPotsProductCountInput(), {
                    potsProductCount: plantProducts.length
                });
                this.setState({ isSharedStateSet: true });
            }
        } else if (nonPlantProducts.length > 0) {
            const inventoryInformation = mapProductInventoryInformation(
                actionContext,
                productAvailabilitesNonPlantsHD?.ProductWarehouseInventoryAvailabilities ?? []
            );
            this.setState({
                inventoryInformation: inventoryInformation,
                productCount: nonPlantProducts.length
            });
            // Check if product shared state is not set, set it
            if (!this.state.isSharedStateSet) {
                this.props.context.actionContext.update(new GetPlantPotsProductCountInput(), {
                    potsProductCount: nonPlantProducts.length
                });
                this.setState({ isSharedStateSet: true });
            }
        }
    };
    // Following function adds product to cart
    private _getAddToCart = (product: ProductSearchResult, itemKey: string) => () => {
        this._addItemToCart(product, itemKey).catch(error => `[addItemToCart] unable to add to cart`);
    };

    private getOrderQuantityLimitsFeatureFlag(): boolean {
        const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
        if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
            return false;
        }

        try {
            const featureStatuses = this.props.data.featureState.result;
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                return false;
            }
        } catch (error) {
            this.props.telemetry.warning(error as string);
            this.props.telemetry.debug('Unable to get feature states');
            return false;
        }

        if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
            return true;
        }
        try {
            const customerInfo = this.props.data.customerInformation.result;
            return (
                !!customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } catch (error) {
            this.props.telemetry.warning(error as string);
            this.props.telemetry.debug('Unable to get customer info');
            return false;
        }
    }

    private _updateCookiesForAddedProducts = (product: ProductSearchResult) => {
        const cookies = this.props.context.actionContext.requestContext.cookies;
        const addedProducts: IDobbiesAddedProductToBundle[] = [];
        const addedProductsToCollection =
            cookies.get(this.props.config.isNotificationModal ? 'notification_added_products' : 'added_products_bundles')?.value || [];
        const maxPDPQuantity = this.getPDPProductMaxQ();
        const maxPDPQNumber = maxPDPQuantity ? Number(maxPDPQuantity) : 0;
        if (Array.isArray(addedProductsToCollection)) {
            for (const obj of addedProductsToCollection) {
                addedProducts.push({
                    productId: obj['productId'],
                    quantity: obj['quantity'],
                    price: obj['price'],
                    maxQuantity: obj['maxQuantity']
                });
            }
        }
        let productAddedAlready = false;
        for (const ap of addedProducts) {
            if (ap.productId === product.RecordId) {
                ap.quantity = ap.quantity ? ap.quantity + 1 : 1;
                productAddedAlready = true;
            }
        }
        if (!productAddedAlready) {
            const prodQD = this.state.currQuantities.find(qd => qd[0] === product.RecordId);
            if (prodQD) {
                const quantityToBeAdded = prodQD[1];
                addedProducts.push({
                    productId: product.RecordId,
                    quantity: quantityToBeAdded,
                    price: product.Price ? product.Price : product.BasePrice,
                    maxQuantity:
                        product.RecordId === this.state.pdpSearchResultProduct?.RecordId
                            ? maxPDPQNumber
                            : this.getMaxAvailableQuantityForProduct(product.RecordId)
                });
            } else {
                addedProducts.push({
                    productId: product.RecordId,
                    quantity: 1,
                    price: product.Price ? product.Price : product.BasePrice,
                    maxQuantity:
                        product.RecordId === this.state.pdpSearchResultProduct?.RecordId
                            ? maxPDPQNumber
                            : this.getMaxAvailableQuantityForProduct(product.RecordId)
                });
            }
        }

        const cookieName = this.props.config.isNotificationModal ? 'notification_added_products' : 'added_products_bundles';
        cookies.set(cookieName, addedProducts);
        this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
        if (this.props.config.isNotificationModal) {
            this.updateAddedProductsList();
            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: false });
        }
    };
    private getMaxAvailableQuantityForProduct = (productId: number): number => {
        let quantity = 0;
        const inventoryInformation = this.state.inventoryInformation;
        const productInvInfo =
            inventoryInformation && inventoryInformation.find(inventory => inventory.ProductAvailableQuantity.ProductId === productId);
        quantity =
            productInvInfo && productInvInfo.ProductAvailableQuantity.AvailableQuantity
                ? productInvInfo.ProductAvailableQuantity.AvailableQuantity
                : 0;
        const product = this.state.simpleProductObjs?.find(prod => prod.RecordId === productId);
        quantity = _getCartItemMaxQuantity(
            this.props.context.app.config.maxQuantityForCartLineItem,
            this.props.context.app.config.enableStockCheck || false,
            quantity,
            this.getOrderQuantityLimitsFeatureFlag(),
            product?.Behavior?.MaximumQuantity || 0
        );
        return quantity;
    };

    private _addItemToCart = async (product: ProductSearchResult, itemKey: string): Promise<void> => {
        const { context, data, resources, telemetry } = this.props;
        const { cart, featureState } = data;
        const { addedToCartSuccessMessage, addedToCartFailureMessage } = resources;
        const { inventoryInformation } = this.state;
        const productInventoryInformation =
            inventoryInformation &&
            inventoryInformation.find(inventory => inventory.ProductAvailableQuantity.ProductId === product.RecordId);
        const navigationUrl = Msdyn365.getUrlSync('cart', context.actionContext);
        if (this.props.config.isProductBundles && !this.props.config.isBundlesModal && !this.props.config.isNotificationModal) {
            this._updateCookiesForAddedProducts(product);
            return;
        }
        if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
            const productList = new Array();
            const p = await this._convertToSimpleProduct(product);
            const prodQD = this.state.currQuantities.find(qd => qd[0] === product.RecordId);
            let quantityToBeAdded = 1;
            if (prodQD) {
                quantityToBeAdded = prodQD[1];
                const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
                if (cl && cl.Quantity && cl.Quantity < quantityToBeAdded) {
                    quantityToBeAdded = quantityToBeAdded - cl.Quantity;
                }
            }
            productList.push({
                product: p,
                count: quantityToBeAdded,
                enableStockCheck: this.props.context.app.config.enableStockCheck
            });
            cart.result
                ?.addProductsToCart(productList)
                .then(result => {
                    if (result.status === 'SUCCESS') {
                        this.setState({
                            productId: product.RecordId,
                            itemKey: itemKey,
                            userMessage: addedToCartSuccessMessage,
                            statusCssString: 'success',
                            messageGlyph: 'fas fa-check',
                            isOpen: true
                        });
                        if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
                            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
                        }
                    }
                })
                .catch(e => {});
            return;
        }
        if (this.props.config.isProductBundles && this.props.config.isNotificationModal) {
            const productList = new Array();
            const p = await this._convertToSimpleProduct(product);
            const prodQD = this.state.currQuantities.find(qd => qd[0] === product.RecordId);
            let quantityToBeAdded = 1;
            if (prodQD) {
                quantityToBeAdded = prodQD[1];
                const cl = this.props.data.cart.result?.cart.CartLines?.find(c => c.ProductId === product.RecordId);
                if (cl && cl.Quantity && cl.Quantity < quantityToBeAdded) {
                    quantityToBeAdded = quantityToBeAdded - cl.Quantity;
                }
            }
            productList.push({
                product: p,
                count: quantityToBeAdded,
                enableStockCheck: this.props.context.app.config.enableStockCheck
            });
            cart.result
                ?.addProductsToCart(productList)
                .then(result => {
                    if (result.status === 'SUCCESS') {
                        this.setState({
                            productId: product.RecordId,
                            itemKey: itemKey,
                            userMessage: addedToCartSuccessMessage,
                            statusCssString: 'success',
                            messageGlyph: 'fas fa-check',
                            isOpen: true
                        });
                        if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
                            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
                        }
                        if (this.props.config.isProductBundles && this.props.config.isNotificationModal) {
                            this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
                        }
                    }
                })
                .catch(e => {});
            return;
        }
        if (context) {
            if (cart.result) {
                const isOrderQuantityLimitsFeatureEnabledInHq =
                    featureState.result?.find(
                        featuresState => featuresState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                    )?.IsEnabled || false;
                const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform
                    ?.enableDefaultOrderQuantityLimits;

                const isOrderQuantityLimitsFeatureEnabledInSiteSettings = defaultOrderQuantityLimitsFeatureConfig === 'all';

                const isOrderQuantityLimitsFeatureEnabled =
                    isOrderQuantityLimitsFeatureEnabledInHq && isOrderQuantityLimitsFeatureEnabledInSiteSettings;
                // check if the product delivery mode is electronic
                const emailDeliveryModeCode = this.props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode;
                const deliveryOptions = await getDeliveryOptionsAsync(
                    { callerContext: this.props.context.actionContext, queryResultSettings: {} },
                    [product.RecordId]
                );

                const simpleProduct = await this._convertToSimpleProduct(product);

                const addProductToCartInput = {
                    product: simpleProduct,
                    quantity: 1,
                    enableStockCheck: context.app.config.enableStockCheck,
                    availableQuantity: productInventoryInformation
                        ? productInventoryInformation.ProductAvailableQuantity.AvailableQuantity
                        : 0,
                    additionalProperties: { orderQuantityLimitsFeatureIsEnabled: isOrderQuantityLimitsFeatureEnabled },
                    isAddEmailDeliveryItemToCart:
                        ArrayExtensions.hasElements(deliveryOptions) &&
                        ArrayExtensions.hasElements(deliveryOptions[0].DeliveryOptions) &&
                        deliveryOptions[0].DeliveryOptions[0].Code === emailDeliveryModeCode
                };
                cart.result
                    .addProductToCart(addProductToCartInput)
                    .then(async result => {
                        if (result.status === 'SUCCESS') {
                            this.setState({
                                productId: product.RecordId,
                                itemKey: itemKey,
                                userMessage: addedToCartSuccessMessage,
                                statusCssString: 'success',
                                messageGlyph: 'fas fa-check',
                                isOpen: true
                            });
                            if (this.props.config.isProductBundles && this.props.config.isBundlesModal) {
                                this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
                            }
                            if (this.props.config.isProductBundles && this.props.config.isNotificationModal) {
                                this.props.context.actionContext.update(new CartModulesStateShareInput(), { isCartUpdated: true });
                            }
                            !this.props.config.isProductBundles &&
                                !this.props.config.isBundlesModal &&
                                navigationUrl &&
                                window &&
                                window.location.assign(navigationUrl);
                        } else if (result.substatus === 'MAXQUANTITY') {
                            const curQuantity =
                                (await cart).cart?.CartLines?.find(item => item.ProductId === product.RecordId)?.Quantity || 0;
                            const maxQtyLimitReachedMessage = this._getInventoryValueMessage(productInventoryInformation, curQuantity);
                            this.setState({
                                productId: product.RecordId,
                                itemKey: itemKey,
                                userMessage: maxQtyLimitReachedMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        } else if (result.substatus === 'QUANTITYLIMITS' && isOrderQuantityLimitsFeatureEnabled) {
                            const maxQtyLimitReachedMessage = result.errorDetails?.LocalizedMessage;
                            this.setState({
                                productId: product.RecordId,
                                itemKey: itemKey,
                                userMessage: maxQtyLimitReachedMessage || addedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        } else {
                            this.props.telemetry.debug('[wishlist-items] Unable to Update Cart Line from Pots Collection');
                            this.setState({
                                productId: product.RecordId,
                                itemKey: itemKey,
                                userMessage: addedToCartFailureMessage,
                                statusCssString: 'danger',
                                messageGlyph: 'fas fa-exclamation-triangle',
                                isOpen: true
                            });
                        }
                    })
                    .catch(error => {
                        telemetry.warning(error);
                        telemetry.debug('[wishlist-items] Error trying to Update Cart Line from Pots Collection');
                        this.setState({
                            productId: product.RecordId,
                            itemKey: itemKey,
                            userMessage: addedToCartFailureMessage,
                            statusCssString: 'danger',
                            messageGlyph: 'fas fa-exclamation-triangle',
                            isOpen: true
                        });
                    });
            }
        }
    };

    private readonly getNonClubPlusDiscountPrices = async (): Promise<void> => {
        const products = this.state.products || [];
        if (this.props.config.isProductBundles && this.state.pdpSearchResultProduct) {
            products.push(this.state.pdpSearchResultProduct);
        }
        const productRecIds = ArrayExtensions.hasElements(products) ? products.map(prod => prod.RecordId) : [];
        const projectDomain: ProjectionDomain = {
            ChannelId: this.props.context.request.apiSettings.channelId,
            CatalogId: this.props.context.request.apiSettings.catalogId
        };
        await getActivePricesAsync(
            {
                callerContext: this.props.context.actionContext,
                queryResultSettings: {}
            },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            null,
            true
        )
            .then(async (activePricesResponse: ProductPrice[]) => {
                if (activePricesResponse && activePricesResponse.length > 0) {
                    this.setState({
                        nonClubPlusDiscount: activePricesResponse
                    });
                    const productsListWithKitScenarioHandled = await this.getProductsListWithKitProductsHandled(
                        this.state.products ? this.state.products : []
                    );
                    const updatedProducts = productsListWithKitScenarioHandled.map(prod => {
                        const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
                        if (activePrice) {
                            prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
                            prod.BasePrice = Math.max(
                                activePrice.BasePrice || 0,
                                activePrice.TradeAgreementPrice || 0,
                                activePrice.AdjustedPrice || 0
                            );
                        }
                        return prod;
                    });
                    const productPricesforcookies = updatedProducts.map(prod => {
                        return {
                            recordId: prod.RecordId,
                            price: prod.Price,
                            basePrice: prod.BasePrice
                        };
                    });
                    if (updatedProducts) {
                        const cookieName = this.props.config.isNotificationModal ? 'notification_products_prices' : '';
                        if (cookieName) {
                            this.props.context.actionContext.requestContext.cookies.set(cookieName, productPricesforcookies);
                        }
                        this.setState({ products: updatedProducts });
                    }
                    const activePrice = activePricesResponse.find(price => price.ProductId === this.state.pdpSearchResultProduct?.RecordId);
                    if (activePrice) {
                        const pdpProd = this.state.pdpSearchResultProduct;
                        if (pdpProd) {
                            pdpProd.Price = activePrice.CustomerContextualPrice ?? pdpProd.Price;
                            pdpProd.BasePrice = Math.max(
                                activePrice.BasePrice || 0,
                                activePrice.TradeAgreementPrice || 0,
                                activePrice.AdjustedPrice || 0
                            );
                            this.setState({ pdpSearchResultProduct: pdpProd });
                            if (this.props.config.isProductBundles && !this.props.config.isBundlesModal) {
                                const cookies = this.props.context.actionContext.requestContext.cookies;
                                const addedProductsToCollection =
                                    cookies.get(
                                        this.props.config.isNotificationModal ? 'notification_added_products' : 'added_products_bundles'
                                    )?.value || [];
                                let pdpProductAlreadyAdded = false;
                                if (Array.isArray(addedProductsToCollection)) {
                                    for (const obj of addedProductsToCollection) {
                                        if (pdpProd.RecordId === obj['productId']) {
                                            pdpProductAlreadyAdded = true;
                                        }
                                    }
                                }
                                if (!pdpProductAlreadyAdded) {
                                    if (this.props.config.isProductBundles && !this.props.config.isBundlesModal) {
                                        this._addItemToCart(pdpProd, pdpProd.RecordId.toString()).catch(
                                            error => `[addItemToCart] unable to add to cart`
                                        );
                                    }
                                }
                            }
                        }
                    }
                }
            })
            .catch((error: Error) => {
                this.props.context.actionContext.trace(error.message);
                this.props.context.actionContext.telemetry.exception(error);
                this.props.context.actionContext.telemetry.debug(
                    '[get Affiliation based price on search result container ]Error executing action'
                );
                throw new Error('[get Affiliation based price on search result container]Error executing action');
            });
    };

    // Following function converts ProductSearchResult into SimpleProduct
    private async _convertToSimpleProduct(product: ProductSearchResult): Promise<SimpleProduct> {
        const {
            context: {
                actionContext: {
                    requestContext: {
                        apiSettings: { channelId }
                    }
                }
            }
        } = this.props;

        const response = await getByIdAsync({ callerContext: this.props.context.actionContext }, product.RecordId, channelId);
        return Array.isArray(response) ? response[0] : response;
    }

    // Followig function returns inventory messages
    private _getInventoryValueMessage(product: IProductInventoryInformation | undefined, curQuantity: number): string {
        const { context, resources } = this.props;
        const { maxQuantityLimitText } = resources;

        const maxQuantityForLineItem = context.app.config.maxQuantityForCartLineItem || 10;

        if (!product || !product.ProductAvailableQuantity.AvailableQuantity || !context.app.config.enableStockCheck) {
            return maxQuantityLimitText
                .replace('{maxQuantity}', maxQuantityForLineItem.toString())
                .replace('{curQuantity}', curQuantity.toString());
        }

        const minInventory = Math.min(maxQuantityForLineItem, product.ProductAvailableQuantity.AvailableQuantity);

        return maxQuantityLimitText.replace('{maxQuantity}', minInventory.toString()).replace('{curQuantity}', curQuantity.toString());
    }
    /* VSI Customization - END */
    // private _getActivePrice = async (): Promise<void> => {
    //     let products = this.state.products || [];
    //     const productRecIds = products && products.length > 0 ? products.map(prod => prod.RecordId) : [];
    //     const projectDomain: ProjectionDomain = {
    //         ChannelId: this.props.context.request.apiSettings.channelId,
    //         CatalogId: this.props.context.request.apiSettings.catalogId
    //     };
    //     const affliations: AffiliationLoyaltyTier[] = [];
    //     const customerInput = new GetCustomerInput(this.props.context.actionContext.requestContext.apiSettings);
    //     const customer = await getCustomer(customerInput, this.props.context.actionContext);

    //     if (customer && customer.CustomerAffiliations) {
    //         customer.CustomerAffiliations.forEach((affliation: CustomerAffiliation) => {
    //             const aff: AffiliationLoyaltyTier = { AffiliationId: affliation.RetailAffiliationId, CustomerId: customer.AccountNumber };
    //             affliations.push(aff);
    //         });
    //     }

    //     await getActivePricesAsync(
    //         { callerContext: this.props.context.actionContext, queryResultSettings: {} },
    //         projectDomain,
    //         productRecIds,
    //         new Date(),
    //         null,
    //         affliations,
    //         true
    //     )
    //         .then((activePricesResponse: ProductPrice[]) => {
    //             this.productPrices = activePricesResponse;
    //             products = products.map(prod => {
    //                 const activePrice = activePricesResponse.find(price => price.ProductId === prod.RecordId);
    //                 if (activePrice) {
    //                     prod.Price = activePrice.CustomerContextualPrice ?? prod.Price;
    //                     prod.BasePrice = Math.max(
    //                         activePrice.BasePrice || 0,
    //                         activePrice.TradeAgreementPrice || 0,
    //                         activePrice.AdjustedPrice || 0
    //                     );
    //                 }
    //                 return prod;
    //             });
    //             this.setState({
    //                 products: products
    //             });
    //         })
    //         .catch((error: Error) => {
    //             this.props.context.actionContext.trace(error.message);
    //             this.props.context.actionContext.telemetry.exception(error);
    //             this.props.context.actionContext.telemetry.debug(
    //                 `[get Affiliation based price on Product Collection]Error executing action`
    //             );
    //             throw new Error('[get Affiliation based price on Product Collection]Error executing action');
    //         });
    // };
}

export default ProductCollection;
